/**
 * Palettes — curated, not generated.
 *
 * Each palette is a closed set of six roles. Templates reference roles
 * (`p.ink`, `p.accent`), never raw hex, so a palette can be swapped or
 * re-tuned without touching a single layout.
 *
 *   bg              page field
 *   surface         a raised/inset panel sitting on bg
 *   ink             primary text on bg — must clear 4.5:1
 *   inkMuted        secondary text on bg — must clear 4.5:1 at body size
 *   surfaceInk      primary text on surface — must clear 4.5:1
 *   surfaceInkMuted secondary text on surface
 *   accent          the one saturated colour; rules, badges, CTA fills
 *   accentInk       text placed ON accent — must clear 4.5:1 against accent
 *   accentDeep      a darkened accent for accent-coloured TEXT on a light
 *                   surface, where the display accent is too pale to read
 *
 * A palette whose surface tracks its bg (both dark, or both light) can omit
 * the surface inks — they default to the bg inks.
 *
 * Contrast is not a matter of taste here: `__tests__/library.test.js` computes
 * WCAG ratios for every role pair and fails the build on a miss.
 */

/** Relative luminance per WCAG 2.1. */
const luminance = (hex) => {
  const m = hex.replace('#', '');
  const full = m.length === 3 ? m.split('').map((c) => c + c).join('') : m;
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16) / 255);
  const lin = (c) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
};

/** WCAG contrast ratio between two hex colours. 1 = identical, 21 = max. */
export const contrastRatio = (a, b) => {
  const [l1, l2] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (l1 + 0.05) / (l2 + 0.05);
};

const palette = (id, name, roles) => ({
  id,
  name,
  ...roles,
  surfaceInk: roles.surfaceInk ?? roles.ink,
  surfaceInkMuted: roles.surfaceInkMuted ?? roles.inkMuted,
  accentDeep: roles.accentDeep ?? roles.accent,
});

export const PALETTES = {
  /** Deep navy field, bone type, brushed-brass accent. Luxury listings. */
  'navy-brass': palette('navy-brass', 'Navy & Brass', {
    bg: '#0E1C2B',
    surface: '#16293D',
    ink: '#F6F2EA',
    inkMuted: '#B4C1CE',
    accent: '#C9A55F',
    accentInk: '#0E1C2B',
  }),

  /** Warm paper, near-black ink, tobacco accent. Editorial / print-first. */
  'bone-editorial': palette('bone-editorial', 'Bone Editorial', {
    bg: '#F3EFE7',
    surface: '#FFFFFF',
    ink: '#1B1A17',
    inkMuted: '#5E594F',
    accent: '#8A6234',
    accentInk: '#FFFFFF',
  }),

  /** Pure white, black ink, one clay-red hit. Swiss / modern minimal. */
  'swiss-clay': palette('swiss-clay', 'Swiss Clay', {
    bg: '#FFFFFF',
    surface: '#F2F2F0',
    ink: '#0D0D0D',
    inkMuted: '#5C5C5C',
    accent: '#B23A16',
    accentInk: '#FFFFFF',
  }),

  /** Forest green field, cream type, soft gold. Estate / heritage. */
  'emerald-gold': palette('emerald-gold', 'Emerald & Gold', {
    bg: '#0B342C',
    surface: '#12463C',
    ink: '#F1EDE2',
    inkMuted: '#A9C2B8',
    accent: '#D2AE6D',
    accentInk: '#0B342C',
  }),

  /** Warm stone, espresso ink, bronze accent. Classic residential. */
  'stone-bronze': palette('stone-bronze', 'Stone & Bronze', {
    bg: '#EDE7DE',
    surface: '#FBF8F3',
    ink: '#2A2622',
    inkMuted: '#6A6158',
    accent: '#8C6239',
    accentInk: '#FBF8F3',
  }),

  /**
   * Deep navy field, champagne gold, bone panels — the Elite Realty house
   * palette. Every value here was sampled from the reference sheets by
   * scripts/measure_refs.py rather than judged by eye. Unlike the palettes above, its surface inverts against its bg
   * (light panels on a dark field), so it carries its own surface inks; and
   * `accentDeep` exists because the display gold is too pale to set small
   * type on a bone panel.
   */
  'elite-navy-gold': palette('elite-navy-gold', 'Elite Navy & Gold', {
    bg: '#02121E',
    surface: '#F1EFEC',
    ink: '#FFFFFF',
    inkMuted: '#C2CBD6',
    surfaceInk: '#02121E',
    surfaceInkMuted: '#59616E',
    accent: '#B8914F',
    accentInk: '#02121E',
    accentDeep: '#7E6020',
  }),
};

export const getPalette = (id) => {
  const p = PALETTES[id];
  if (!p) throw new Error(`templateLibrary: unknown palette "${id}"`);
  return p;
};

/** Translucent overlay used to float type over a photograph. */
export const scrim = (hex, alpha) => {
  const m = hex.replace('#', '');
  const full = m.length === 3 ? m.split('').map((c) => c + c).join('') : m;
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16));
  return `rgba(${r},${g},${b},${alpha})`;
};
